<template>
    <Spinner v-if="displaySpinner" />
    <div v-if="enrollment" class="enrollment">
        <Message v-if="!totpSecret" severity="error">
            Could not generate a secret key. Please try again.
        </Message>
        <label class="text-l">
            1. Scan the following QR code with your Authenticator app.
        </label>
        <img :src="qrCode" />
        <p>Or use the following secret:<br />{{ totpSecret?.secretKey }}</p>
    </div>
    <form class="otp">
        <label for="otp" class="text-l">
            <span v-if="enrollment">2. </span>
            Fill in the one-time password from your Authenticator app.
        </label>
        <Message v-if="errorMessage" :closable="false" severity="error">
            {{ errorMessage }}
        </Message>
        <Message v-if="successMessage" :closable="false" severity="success">
            {{ successMessage }}
        </Message>
        <InputOtp
            @paste="verify"
            @keydown.enter="verify"
            autocomplete="one-time-code"
            autofocus
            id="otp"
            :length="6"
            v-model="otp"
        />
        <div class="buttons">
            <Button label="Submit" raised primary @click="verify" />
            <Button label="Back" raised primary @click="back" />
        </div>
    </form>
</template>

<script lang="ts" setup>
import { inject, Ref, ref, watch } from "vue";
import Spinner from "@/shared/Spinner.vue";
import {
    AuthStatus,
    useAuthenticationManager,
    useAuthenticationStore,
} from "@/stores/authentication-store";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import { TotpSecret } from "firebase/auth";
import { useQRCode } from "@vueuse/integrations/useQRCode";

const dialogRef = inject<Ref<DynamicDialogInstance>>("dialogRef");
const { signOut, verifyTotpEnrollment, verifyOtp, getTotpSecret } =
    useAuthenticationStore();
const { authStatus } = useAuthenticationManager();

const displaySpinner = ref(false);
const otp = ref("");
const qrDataUrl = ref("");
const errorMessage = ref("");
const successMessage = ref("");
const qrCode = useQRCode(() => qrDataUrl.value, {
    errorCorrectionLevel: "H",
});
const totpSecret = ref<TotpSecret>();
const { enrollment, successCallback, failureCallback } = dialogRef!.value.data;

const refreshTotpSecrets = async () => {
    getTotpSecret().then(async (secrets) => {
        if (secrets) {
            totpSecret.value = secrets.totpSecret;
            qrDataUrl.value = secrets.totpUri;
        }
    });
};

watch(
    authStatus,
    async (newAuthStatus, oldAuthStatus) => {
        if (
            newAuthStatus === AuthStatus.MFA_ENROLLMENT_REQUIRED &&
            newAuthStatus !== oldAuthStatus &&
            !totpSecret.value
        ) {
            await refreshTotpSecrets();
        }
    },
    { immediate: true }
);

const close = () => dialogRef!.value.close();
const verify = async () => {
    displaySpinner.value = true;
    try {
        if (enrollment) {
            await verifyTotpEnrollment(otp.value);
            successMessage.value =
                "Succesfully set up your Authenticator app. From now on, you must login using the authenticator app.";
        } else {
            await verifyOtp(otp.value);
        }
        successCallback();
    } catch (error) {
        errorMessage.value = "Invalid verification code.";
        failureCallback();
        throw error;
    } finally {
        displaySpinner.value = false;
    }
    // Give user time to read success message.
    if (successMessage.value) {
        await new Promise((resolve) => {
            setTimeout(resolve, 1000);
        });
    }
    close();
};

const back = async () => {
    await signOut();
    close();
};
</script>

<style lang="scss">
.text-l {
    font-size: 1.5rem;
}
.enrollment {
    margin-bottom: 2rem;
    text-align: center;

    img {
        margin: auto;
    }
}

#otp {
    justify-content: center;
    margin: 2rem auto;
}

.otp {
    text-align: center;
}

.buttons {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    justify-content: end;
}
</style>
